import type { MapGeometry, MapRouting } from "../shared/types";
import {
  barrierPoint,
  type RoutingPoint,
  type RoutingSelection,
} from "./routingEditorGeometry";

export interface RoutingWallSuggestion {
  id: string;
  start: RoutingPoint;
  end: RoutingPoint;
  length: number;
}

type Segment = { start: RoutingPoint; end: RoutingPoint };
const EPSILON = 1e-7;
const MAX_VERTICES = 40_000;
const MAX_CANDIDATES = 2_048;
const MAX_SUGGESTIONS = 128;
const MAX_BARRIERS = 256;
const distance = (a: RoutingPoint, b: RoutingPoint) =>
  Math.hypot(a[0] - b[0], a[1] - b[1]);
const finitePoint = (p: number[]): p is RoutingPoint =>
  p.length === 2 && p.every(Number.isFinite);
const comparePoints = (a: RoutingPoint, b: RoutingPoint) =>
  a[0] - b[0] || a[1] - b[1];
const roundPoint = (p: RoutingPoint): RoutingPoint =>
  p.map((value) => Math.round(value * 1_000) / 1_000) as RoutingPoint;

function canonical(segment: Segment, floor: MapGeometry["floor"]): Segment {
  const rounded = (point: RoutingPoint): RoutingPoint => {
    const [x, y] = roundPoint(point);
    return [
      Math.max(floor.x, Math.min(floor.x + floor.width, x)),
      Math.max(floor.y, Math.min(floor.y + floor.height, y)),
    ];
  };
  const a = rounded(segment.start),
    b = rounded(segment.end);
  return comparePoints(a, b) <= 0 ? { start: a, end: b } : { start: b, end: a };
}

/** Intersect the original segment with the floor, without projecting it onto an edge. */
function clip(segment: Segment, floor: MapGeometry["floor"]): Segment | null {
  const [x, y] = segment.start;
  const dx = segment.end[0] - x,
    dy = segment.end[1] - y;
  let from = 0,
    to = 1;
  for (const [p, q] of [
    [-dx, x - floor.x],
    [dx, floor.x + floor.width - x],
    [-dy, y - floor.y],
    [dy, floor.y + floor.height - y],
  ]) {
    if (Math.abs(p) <= EPSILON) {
      if (q < 0) return null;
      continue;
    }
    const t = q / p;
    if (p < 0) from = Math.max(from, t);
    else to = Math.min(to, t);
    if (from > to) return null;
  }
  return {
    start: [x + dx * from, y + dy * from],
    end: [x + dx * to, y + dy * to],
  };
}

function contiguous(a: Segment, b: Segment): boolean {
  if (distance(a.end, b.start) > EPSILON) return false;
  const dx = a.end[0] - a.start[0],
    dy = a.end[1] - a.start[1];
  const ex = b.end[0] - b.start[0],
    ey = b.end[1] - b.start[1];
  return dx * ex + dy * ey > 0 && Math.abs(dx * ey - dy * ex) <= EPSILON;
}

/** Only remove a line whose complete span is already represented. Never bridge a gap. */
function coveredBy(
  candidate: Segment,
  other: Segment,
  tolerance: number,
): boolean {
  const dx = other.end[0] - other.start[0],
    dy = other.end[1] - other.start[1];
  const length = Math.hypot(dx, dy);
  const cx = candidate.end[0] - candidate.start[0],
    cy = candidate.end[1] - candidate.start[1];
  const candidateLength = Math.hypot(cx, cy);
  if (
    length <= EPSILON ||
    candidateLength <= EPSILON ||
    Math.abs(dx * cy - dy * cx) / (length * candidateLength) > 0.015
  )
    return false;
  return [candidate.start, candidate.end].every(([x, y]) => {
    const along =
      ((x - other.start[0]) * dx + (y - other.start[1]) * dy) / length;
    const across =
      Math.abs((x - other.start[0]) * dy - (y - other.start[1]) * dx) / length;
    return (
      across <= tolerance && along >= -tolerance && along <= length + tolerance
    );
  });
}

/**
 * Find reviewable straight-line candidates in imported architectural ink.
 * These are suggestions, not classified walls: no inferred doors, connected gaps,
 * floor restrictions, document mutation or source-network requests.
 */
export function suggestRoutingWalls(
  geometry: MapGeometry,
  routing: MapRouting,
): { walls: RoutingWallSuggestion[]; truncated: boolean } {
  const { floor } = geometry;
  const extent = Math.min(floor.width, floor.height);
  if (!Number.isFinite(extent) || extent <= 0 || !geometry.architecture?.length)
    return { walls: [], truncated: false };
  const minimumLength = Math.max(1, extent * 0.06);
  const candidates: RoutingWallSuggestion[] = [];
  let vertices = 0,
    truncated = false;
  outer: for (const polygon of geometry.architecture) {
    for (const ring of polygon) {
      if ((vertices += ring.length) > MAX_VERTICES) {
        truncated = true;
        break outer;
      }
      if (ring.length < 3) continue;
      const runs: Segment[] = [];
      for (let index = 0; index < ring.length; index++) {
        const start = ring[index],
          end = ring[(index + 1) % ring.length];
        if (
          !finitePoint(start) ||
          !finitePoint(end) ||
          distance(start, end) <= EPSILON
        )
          continue;
        const next: Segment = { start: [...start], end: [...end] };
        const previous = runs[runs.length - 1];
        if (previous && contiguous(previous, next)) previous.end = next.end;
        else runs.push(next);
      }
      if (runs.length > 1 && contiguous(runs[runs.length - 1], runs[0])) {
        runs[0].start = runs.pop()!.start;
      }
      for (const run of runs) {
        const clipped = clip(run, floor);
        if (!clipped) continue;
        const segment = canonical(clipped, floor);
        const length = distance(segment.start, segment.end);
        if (length < minimumLength) continue;
        candidates.push({
          ...segment,
          id: `source-wall:${[...segment.start, ...segment.end].join(":")}`,
          length,
        });
      }
    }
  }
  candidates.sort(
    (a, b) =>
      b.length - a.length ||
      comparePoints(a.start, b.start) ||
      comparePoints(a.end, b.end),
  );
  if (candidates.length > MAX_CANDIDATES) truncated = true;
  const filtered = filterRoutingWallSuggestions(
    candidates.slice(0, MAX_CANDIDATES),
    routing,
    geometry,
  );
  return { walls: filtered.walls, truncated: truncated || filtered.truncated };
}

/** Reuse extracted lines as the draft changes; no source-contour scan is needed. */
export function filterRoutingWallSuggestions(
  candidates: RoutingWallSuggestion[],
  routing: MapRouting,
  geometry?: MapGeometry,
): { walls: RoutingWallSuggestion[]; truncated: boolean } {
  const extent = geometry
    ? Math.min(geometry.floor.width, geometry.floor.height)
    : 0;
  const tolerance = Math.max(0.001, extent * 0.002);
  const capacity = Math.max(
    0,
    Math.min(MAX_SUGGESTIONS, MAX_BARRIERS - routing.barriers.length),
  );
  const walls: RoutingWallSuggestion[] = [];
  let truncated = candidates.length > MAX_CANDIDATES;
  for (const candidate of candidates.slice(0, MAX_CANDIDATES)) {
    if (
      routing.barriers.some(
        (wall) =>
          wall.id === candidate.id || coveredBy(candidate, wall, tolerance),
      ) ||
      walls.some((wall) => coveredBy(candidate, wall, tolerance))
    )
      continue;
    if (walls.length >= capacity) {
      truncated = true;
      break;
    }
    walls.push(candidate);
  }
  return { walls, truncated };
}

/** Snap within a caller-supplied source-unit tolerance, normally derived from screen pixels. */
export function snapRoutingPoint(
  point: RoutingPoint,
  routing: MapRouting,
  suggestions: RoutingWallSuggestion[],
  tolerance: number,
  exclude?: { selection: RoutingSelection; ring: number; vertex: number },
): { point: RoutingPoint; snapped: boolean } {
  if (!finitePoint(point) || !Number.isFinite(tolerance) || tolerance <= 0)
    return { point: [...point], snapped: false };
  let best: RoutingPoint | undefined;
  let bestDistance = tolerance;
  const consider = (candidate: RoutingPoint) => {
    const d = distance(point, candidate);
    if (d <= tolerance && (!best || d < bestDistance - EPSILON)) {
      best = [...candidate];
      bestDistance = d;
    }
  };
  const selectedDoor =
    exclude?.selection.kind === "door"
      ? routing.doors.find((door) => door.id === exclude.selection.id)
      : undefined;
  const ownWallId =
    exclude?.selection.kind === "barrier"
      ? exclude.selection.id
      : selectedDoor?.barrierId;
  const barriersById = new Map(routing.barriers.map((wall) => [wall.id, wall]));
  const ownWall = ownWallId ? barriersById.get(ownWallId) : undefined;
  const segments: Segment[] = [];
  for (const area of routing.walkableAreas) {
    area.polygon.forEach((ring, ringIndex) =>
      ring.forEach((vertex, index) => {
        if (
          exclude?.selection.kind === "area" &&
          area.id === exclude.selection.id &&
          ringIndex === exclude.ring &&
          index === exclude.vertex
        )
          return;
        consider(vertex as RoutingPoint);
      }),
    );
  }
  for (const wall of routing.barriers) {
    const editingWall =
      exclude?.selection.kind === "barrier" && wall.id === exclude.selection.id;
    if (!editingWall || exclude.vertex !== 0) consider(wall.start);
    if (!editingWall || exclude.vertex !== 1) consider(wall.end);
    if (wall.id !== ownWallId) segments.push(wall);
  }
  for (const door of routing.doors) {
    const wall = barriersById.get(door.barrierId);
    if (
      !wall ||
      (exclude?.selection.kind === "barrier" && wall.id === ownWallId)
    )
      continue;
    [door.start, door.end].forEach((fraction, index) => {
      if (
        exclude?.selection.kind === "door" &&
        door.id === exclude.selection.id &&
        exclude.vertex === index
      )
        return;
      consider(barrierPoint(wall, fraction));
    });
  }
  for (const suggestion of suggestions) {
    // An accepted source line can still be in the overlay while its vertex moves.
    if (
      ownWall &&
      coveredBy(suggestion, ownWall, EPSILON) &&
      coveredBy(ownWall, suggestion, EPSILON)
    )
      continue;
    consider(suggestion.start);
    consider(suggestion.end);
    segments.push(suggestion);
  }
  for (const segment of segments) {
    const dx = segment.end[0] - segment.start[0],
      dy = segment.end[1] - segment.start[1];
    const lengthSquared = dx * dx + dy * dy;
    if (!lengthSquared) continue;
    const t = Math.max(
      0,
      Math.min(
        1,
        ((point[0] - segment.start[0]) * dx +
          (point[1] - segment.start[1]) * dy) /
          lengthSquared,
      ),
    );
    consider([segment.start[0] + t * dx, segment.start[1] + t * dy]);
  }
  return { point: best ?? [...point], snapped: best !== undefined };
}
